import { useAuth, authorizedFetch, API_ORIGIN } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type RoleSeniority = '' | 'junior' | 'mid' | 'senior' | 'lead' | 'executive'
export type RoleStatus = 'draft' | 'active' | 'archived'

export const ROLE_SENIORITY_OPTIONS: { value: Exclude<RoleSeniority, ''>; label: string }[] = [
  { value: 'junior', label: 'Junior' },
  { value: 'mid', label: 'Mid' },
  { value: 'senior', label: 'Senior' },
  { value: 'lead', label: 'Lead' },
  { value: 'executive', label: 'Executive' },
]

export interface CompetencyLevel {
  ordinal: number
  label: string
  anchor: string
}

export interface Competency {
  id: string
  key: string
  label: string
  definition: string
  levels: CompetencyLevel[]
  skills: string[]
  status: 'active' | 'archived'
}

export interface RoleSourceBrief {
  id: string
  title: string
}

export interface RoleCompetency {
  id: string
  competency: Competency
  target_level: number
  target_label: string
  weight: number | null
  rationale: string
  source_field: string
  source_excerpt: string
  sort_order: number
}

export interface Role {
  id: string
  title: string
  family: string
  seniority: RoleSeniority
  department: string
  summary: string
  status: RoleStatus
  competency_count: number
  updated_at: string
}

export interface RoleDetail extends Role {
  competencies: RoleCompetency[]
  source_briefs: RoleSourceBrief[]
}

export interface RoleFields {
  title?: string
  family?: string
  seniority?: RoleSeniority
  department?: string
  summary?: string
  status?: RoleStatus
}

export interface NewCompetency {
  label: string
  definition?: string
  skills?: string[]
}

const ROLES_KEY = ['roles']
const LIBRARY_KEY = ['role-competencies']

export function roleSeniorityLabel(seniority: RoleSeniority): string {
  return ROLE_SENIORITY_OPTIONS.find((o) => o.value === seniority)?.label ?? ''
}

export function roleStatusBadgeVariant(status: RoleStatus) {
  if (status === 'active') return 'successSoft' as const
  if (status === 'archived') return 'secondary' as const
  return 'secondarySoft' as const
}

export function roleStatusLabel(status: RoleStatus) {
  return status.charAt(0).toUpperCase() + status.slice(1)
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await authorizedFetch(`${API_ORIGIN}/api/roles${path}`, init)
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error((data as { error?: string }).error || 'Request failed')
  }
  return data as T
}

function json(method: string, body: unknown): RequestInit {
  return { method, body: JSON.stringify(body) }
}

export function useRoles() {
  const { token } = useAuth()
  const query = useQuery({
    queryKey: ROLES_KEY,
    queryFn: () => request<Role[]>('/'),
    enabled: !!token,
    staleTime: 30 * 1000,
  })
  return { roles: query.data ?? [], isLoading: query.isLoading, error: query.error }
}

export function useRole(roleId: string | undefined) {
  const { token } = useAuth()
  const query = useQuery({
    queryKey: ['role', roleId],
    queryFn: () => request<RoleDetail>(`/${roleId}/`),
    enabled: !!token && !!roleId,
    staleTime: 30 * 1000,
  })
  return { role: query.data, isLoading: query.isLoading, error: query.error }
}

export function useCompetencyLibrary() {
  const { token } = useAuth()
  const query = useQuery({
    queryKey: LIBRARY_KEY,
    queryFn: () => request<Competency[]>('/competencies/'),
    enabled: !!token,
    staleTime: 60 * 1000,
  })
  return { competencies: query.data ?? [], isLoading: query.isLoading }
}

function useRoleMutation<V>(fn: (vars: V) => Promise<RoleDetail>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (role) => {
      queryClient.setQueryData(['role', role.id], role)
      queryClient.invalidateQueries({ queryKey: ROLES_KEY })
    },
  })
}

export function useCreateRole() {
  return useRoleMutation((fields: RoleFields) => request<RoleDetail>('/', json('POST', fields)))
}

export function useUpdateRole() {
  return useRoleMutation(({ id, fields }: { id: string; fields: RoleFields }) =>
    request<RoleDetail>(`/${id}/`, json('PATCH', fields)),
  )
}

export function useDuplicateRole() {
  return useRoleMutation(({ id, seniority }: { id: string; seniority: RoleSeniority }) =>
    request<RoleDetail>(`/${id}/duplicate/`, json('POST', { seniority })),
  )
}

export function useArchiveRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => request<{ success: boolean }>(`/${id}/`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ROLES_KEY }),
  })
}

export function useAddRoleCompetency() {
  const queryClient = useQueryClient()
  return useRoleMutation(
    async ({
      roleId,
      competencyId,
      newCompetency,
      targetLevel,
      rationale,
    }: {
      roleId: string
      competencyId?: string
      newCompetency?: NewCompetency
      targetLevel: number
      rationale?: string
      sourceField?: string
      sourceExcerpt?: string
    }) => {
      let id = competencyId
      if (!id && newCompetency) {
        const created = await request<Competency>('/competencies/', json('POST', newCompetency))
        queryClient.invalidateQueries({ queryKey: LIBRARY_KEY })
        id = created.id
      }
      return request<RoleDetail>(
        `/${roleId}/competencies/`,
        json('POST', {
          competency_id: id,
          target_level: targetLevel,
          rationale: rationale ?? '',
          source_field: sourceField ?? '',
          source_excerpt: sourceExcerpt ?? '',
        }),
      )
    },
  )
}

export function useReorderRoleCompetencies() {
  return useRoleMutation(({ roleId, orderedIds }: { roleId: string; orderedIds: string[] }) =>
    request<RoleDetail>(`/${roleId}/reorder-competencies/`, json('POST', { ordered_ids: orderedIds })),
  )
}

export function useUpdateRoleCompetency() {
  return useRoleMutation(
    ({
      roleId,
      rcId,
      changes,
    }: {
      roleId: string
      rcId: string
      changes: { target_level?: number; rationale?: string }
    }) => request<RoleDetail>(`/${roleId}/competencies/${rcId}/`, json('PATCH', changes)),
  )
}

export function useRemoveRoleCompetency() {
  return useRoleMutation(({ roleId, rcId }: { roleId: string; rcId: string }) =>
    request<RoleDetail>(`/${roleId}/competencies/${rcId}/`, { method: 'DELETE' }),
  )
}
